/**
 * Engineer Home tab (DEV-46921).
 *
 * Three vertically-stacked sections above the bottom nav:
 *   1. Favourites — horizontal carousel of tiles + "View All (n)" link.
 *   2. Recently Purchased — carousel of tiles + "View All" link.
 *   3. Recent Orders — last three orders as a card list + "View All" link.
 *
 * Postcode gate: if no engineer session is set we bounce to JobSetup;
 * once a postcode is present JobSetup itself sends the engineer here.
 * Tile listings are resolved server-side against the current postcode
 * (Noreen 16-07-26: "based on current postcode, not where they were
 * when they favourited").
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Heart, History, Clock, Package, ClipboardList } from 'lucide-react'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { StatusBadge } from '../../components/radix'
import { ProductTile } from '../../components/engineer/ProductTile'
import { WarehousePortalLink } from '../../components/engineer/WarehousePortalLink'
import { WhatsNewButton } from '../../components/engineer/WhatsNewButton'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { useRecentlyPurchased } from '../../auth/hooks/useRecentlyPurchased'
import { useCarouselStock } from '../../auth/hooks/useCarouselStock'
import { useOrders } from '../../auth/hooks/useOrders'
import { useMe } from '../../auth/hooks/useMe'
import { mapOrderForEngineer } from '../../services/order-mappers'
import { formatDateTimeSmart } from '../../utils/datetime'
import { formatMoney } from '../../utils/currency'
import { ProductViewSource } from '../../lib/analytics'
import type { EngineerOrder } from '../../types'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

// Number of tiles shown in each Home carousel. The full list is
// accessible via "View All" — this cap keeps the initial paint fast
// even if the engineer has many favourites / recent purchases.
const HOME_CAROUSEL_LIMIT = 10
// Number of recent orders shown as cards. Client requirement per
// Noreen 16-07-26 call notes.
const RECENT_ORDERS_LIMIT = 3

export function HomePage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const [session, setSession] = useState<EngineerSession | null>(null)
  const [now] = useState(() => new Date())
  const { data: me } = useMe()

  // Postcode gate — Noreen 16-07-26 "if postcode not setup we have to
  // take it on the most priority". Without a session or a stored
  // last_postcode there's nothing to render, so bounce back to
  // JobSetup where the engineer can enter one.
  useEffect(() => {
    const raw = sessionStorage.getItem('engineerSession')
    if (raw) {
      try {
        setSession(JSON.parse(raw) as EngineerSession)
        return
      } catch {
        // Fall through — corrupt session data, treat as absent.
      }
    }
    navigate(`/${tenantSlug}/engineer`, { replace: true })
  }, [navigate, tenantSlug])

  const { favourites, isLoading: favLoading } = useFavourites()
  const { data: recent, isLoading: recentLoading } = useRecentlyPurchased()
  const { data: ordersResponse, isLoading: ordersLoading } = useOrders(
    undefined,
    1,
    true,
    RECENT_ORDERS_LIMIT,
  )

  const recentOrders: EngineerOrder[] = useMemo(
    () => (ordersResponse?.results || []).map(mapOrderForEngineer),
    [ordersResponse],
  )
  const carouselFavourites = favourites.slice(0, HOME_CAROUSEL_LIMIT)
  const carouselRecent = (recent ?? []).slice(0, HOME_CAROUSEL_LIMIT)

  // Collect the listings that need a live stock check. Only recently-purchased
  // tiles show a stock pill; favourite tiles intentionally omit it (DEV-46921
  // mockup). Filter out nulls before passing to the hook.
  const recentListingsForStockCheck = useMemo(
    () => carouselRecent.map((item) => item.listing),
    [carouselRecent],
  )

  // Live stock check — one getLiveStock call per supplier in the carousel,
  // cached 5 min. Returns a Map<rawProductId, StockStatus> that overrides
  // the server-cached stockStatus on each tile. While any supplier's call
  // is in-flight the tile shows its last-known status (no flash to 'checking').
  const liveStockMap = useCarouselStock(recentListingsForStockCheck, session)

  if (!session) {
    // Effect above will redirect — render nothing in the meantime.
    return null
  }

  const firstName = me?.first_name || ''
  const hour = now.getHours()
  const partOfDay = hour < 12 ? 'Morning' : hour < 18 ? 'Afternoon' : 'Evening'
  const greeting = firstName ? `${partOfDay}, ${firstName}` : partOfDay

  return (
    <EngineerLayout>
      <main className="bg-gray-50 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <WhatsNewButton />
          <h1 className="text-gray-900 text-2xl font-bold mb-8">{greeting}</h1>

          {/* ============================================================
              Favourites
              ============================================================ */}
          <section className="mb-8" aria-labelledby="home-favourites-heading">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-brand-primary" />
                <h2
                  id="home-favourites-heading"
                  className="text-gray-900 text-xl font-bold"
                >
                  Favourites
                </h2>
              </div>
              {favourites.length > 0 && (
                <button
                  onClick={() => navigate(`/${tenantSlug}/engineer/favourites`)}
                  className="text-brand-primary text-sm font-medium"
                >
                  View All ({favourites.length})
                </button>
              )}
            </div>
            <p className="text-gray-500 text-sm mb-4">
              Items you&rsquo;ve saved while browsing
            </p>

            {favLoading ? (
              <SkeletonCarousel />
            ) : carouselFavourites.length > 0 ? (
              <div className="carousel-scroll flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-2">
                {carouselFavourites.map((fav) =>
                  fav.listing ? (
                    <ProductTile
                      key={fav.id}
                      listing={fav.listing}
                      viewSource={ProductViewSource.FAVOURITE}
                    />
                  ) : null,
                )}
              </div>
            ) : (
              <div className="-mx-4 bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-gray-900 text-base font-bold mb-1">
                  No favourites yet
                </h3>
                <p className="text-gray-500 text-sm mb-4">
                  Tap the heart icon on any product while searching to save it
                  here
                </p>
                <button
                  onClick={() => navigate(`/${tenantSlug}/engineer/search`)}
                  className="border-2 border-brand-primary text-brand-primary font-semibold text-sm h-10 px-4 rounded-xl"
                >
                  Search products
                </button>
              </div>
            )}
          </section>

          {/* ============================================================
              Recently Purchased
              ============================================================ */}
          <section className="mb-8" aria-labelledby="home-recent-heading">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-brand-primary" />
                <h2
                  id="home-recent-heading"
                  className="text-gray-900 text-xl font-bold"
                >
                  Recently purchased
                </h2>
              </div>
              {(recent?.length ?? 0) > 0 && (
                <button
                  onClick={() =>
                    navigate(`/${tenantSlug}/engineer/recently-purchased`)
                  }
                  className="text-brand-primary text-sm font-medium"
                >
                  View all ({recent!.length})
                </button>
              )}
            </div>
            <p className="text-gray-500 text-sm mb-4">
              Quick re-order from your previous purchases
            </p>

            {recentLoading ? (
              <SkeletonCarousel />
            ) : carouselRecent.length > 0 ? (
              <div className="carousel-scroll flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-2">
                {carouselRecent.map((item) => {
                  const rawId = item.listing.rawProductId
                  const liveStatus = rawId != null ? liveStockMap.get(rawId) : undefined
                  return (
                    <ProductTile
                      key={`rp-${item.orderItemId}`}
                      listing={
                        liveStatus != null
                          ? { ...item.listing, stockStatus: liveStatus }
                          : item.listing
                      }
                      timestampLabel={formatRecentTimestamp(item.purchasedAt)}
                    />
                  )
                })}
              </div>
            ) : (
              <div className="-mx-4 bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-gray-900 text-base font-bold mb-1">
                  Nothing purchased yet
                </h3>
                <p className="text-gray-500 text-sm">
                  Items from your orders will show up here for quick re-order
                </p>
              </div>
            )}
          </section>

          {/* ============================================================
              Recent Orders
              ============================================================ */}
          <section aria-labelledby="home-orders-heading">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand-primary" />
                <h2
                  id="home-orders-heading"
                  className="text-gray-900 text-xl font-bold"
                >
                  Recent orders
                </h2>
              </div>
              {recentOrders.length > 0 && (
                <button
                  onClick={() => navigate(`/${tenantSlug}/engineer/orders`)}
                  className="text-brand-primary text-sm font-medium"
                >
                  View all
                </button>
              )}
            </div>

            {ordersLoading ? (
              <SkeletonOrderList />
            ) : recentOrders.length > 0 ? (
              <div className="-mx-4 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden divide-y divide-gray-100">
                {recentOrders.map((order) => (
                  <button
                    key={order.id}
                    onClick={() =>
                      navigate(`/${tenantSlug}/engineer/order/${order.id}`)
                    }
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-gray-900 font-bold text-sm">
                        {order.supplierName}
                      </span>
                      <StatusBadge status={order.status} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-xs">
                        #{order.orderNumber}
                      </span>
                      <span className="text-gray-900 font-bold text-sm">
                        {formatMoney(order.total)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-400 text-xs mt-1">
                      <Clock className="w-3 h-3" />
                      <span>{formatDateTimeSmart(order.placedAt)}</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="-mx-4 bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <ClipboardList className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-gray-900 text-base font-bold mb-1">
                  No orders yet
                </h3>
                <p className="text-gray-500 text-sm">
                  Your submitted orders will show up here
                </p>
              </div>
            )}

            {/* Wrapper matches the `-mx-4` edge-to-edge pattern used by
             * the Recent Orders card above, so the divider line and the
             * icon indent line up with the card. */}
            <div className="-mx-4">
              <WarehousePortalLink />
            </div>
          </section>
        </div>
      </main>
    </EngineerLayout>
  )
}

/**
 * Compact timestamp label for the Recently-Purchased tile.
 *
 * The mockup shows "13 Jul, 09:15" style stamps in the bottom-right of
 * the tile — short enough to sit next to the price without wrapping.
 * `formatDateTimeSmart` already handles the "Today at HH:MM" case, but
 * that's a bit long for the tile; keep the compact format for that
 * layout.
 */
function formatRecentTimestamp(iso: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const day = d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      timeZone: 'Europe/London',
    })
    const time = d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/London',
    })
    return `${day}, ${time}`
  } catch {
    return ''
  }
}

/**
 * Skeleton carousel — three pulsing placeholder cards that mimic the
 * real `ProductTile` shape (w-60, h-28 image slab + text lines).
 * Shown while favourites or recently-purchased data is loading.
 */
function SkeletonCarousel() {
  return (
    <div className="flex gap-4 overflow-hidden -mx-4 pb-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex-shrink-0 w-60 bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse"
        >
          {/* Image slab */}
          <div className="w-full h-28 bg-gray-200" />
          {/* Text area */}
          <div className="p-4 space-y-2.5">
            <div className="h-4 bg-gray-200 rounded w-4/5" />
            <div className="h-3 bg-gray-100 rounded w-3/5" />
            <div className="h-4 bg-gray-200 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Skeleton order list — three pulsing placeholder rows that mimic the
 * real Recent Orders card (supplier name + status pill row, order
 * number + price row, date row). Shown while orders data is loading.
 */
function SkeletonOrderList() {
  return (
    <div className="-mx-4 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden divide-y divide-gray-100 animate-pulse">
      {[0, 1, 2].map((i) => (
        <div key={i} className="px-4 py-3 space-y-2">
          {/* Supplier name + status pill */}
          <div className="flex items-center justify-between">
            <div className="h-4 bg-gray-200 rounded w-2/5" />
            <div className="h-5 bg-gray-100 rounded-full w-24" />
          </div>
          {/* Order number + price */}
          <div className="flex items-center justify-between">
            <div className="h-3 bg-gray-100 rounded w-1/4" />
            <div className="h-4 bg-gray-200 rounded w-16" />
          </div>
          {/* Date */}
          <div className="h-3 bg-gray-100 rounded w-1/3" />
        </div>
      ))}
    </div>
  )
}
